import { useState } from 'react'
import { formatStatusLabel } from '../../../lib/format'
import type { StaffScheduleData } from '../../../types/plans'
import { SecretLoginLinkDialog } from '../../SecretLoginLinkDialog'

function visitBadgeClasses(kind?: 'primary' | 'repeat') {
  if (kind === 'primary') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }

  if (kind === 'repeat') {
    return 'border-amber-200 bg-amber-50 text-amber-700'
  }

  return 'border-slate-200 bg-slate-50 text-slate-600'
}

function appointmentStatusColor(code?: number | null, label?: string | null): string {
  const normalized = `${code ?? ''} ${label ?? ''}`.toLowerCase()
  if (normalized.includes('10001933') || normalized.includes('импл')) return 'bg-sky-500'
  if (normalized.includes('10001928') || normalized.includes('леч')) return 'bg-emerald-500'
  if (normalized.includes('4429') || normalized.includes('повтор')) return 'bg-amber-400'
  if (normalized.includes('первич')) return 'bg-fuchsia-500'
  if (normalized.includes('0')) return 'bg-slate-400'
  return 'bg-indigo-400'
}

export function StaffScheduleAppointmentsList({
  appointments,
  canIssueSecretLinks = false,
  autoOpenSecretLinks = false,
  onIssuePatientSecretLink,
}: {
  appointments: StaffScheduleData['appointments']
  canIssueSecretLinks?: boolean
  autoOpenSecretLinks?: boolean
  onIssuePatientSecretLink?: (patientId: number) => Promise<string>
}) {
  const [issuingPatientId, setIssuingPatientId] = useState<number | null>(null)
  const [issuedLink, setIssuedLink] = useState<{ url: string; targetName: string } | null>(null)

  async function handleIssuePatientLink(patientId: number, patientName: string) {
    if (!onIssuePatientSecretLink) {
      return
    }

    setIssuingPatientId(patientId)
    try {
      const url = await onIssuePatientSecretLink(patientId)
      try {
        await navigator.clipboard.writeText(url)
      } catch {
        /* ignore clipboard errors */
      }
      if (autoOpenSecretLinks) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        setIssuedLink({ url, targetName: patientName })
      }
    } finally {
      setIssuingPatientId(null)
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-slate-300 bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-[1280px] w-full table-fixed border-collapse text-sm">
          <thead>
            <tr className="bg-slate-100 text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
              <th className="w-[95px] border border-slate-300 px-3 py-2 text-left">Дата</th>
              <th className="w-[95px] border border-slate-300 px-3 py-2 text-left">Время</th>
              <th className="w-[85px] border border-slate-300 px-3 py-2 text-left">Тип</th>
              <th className="w-[150px] border border-slate-300 px-3 py-2 text-left">Врач</th>
              <th className="w-[170px] border border-slate-300 px-3 py-2 text-left">Пациент</th>
              <th className="w-[280px] border border-slate-300 px-3 py-2 text-left">Услуга</th>
              <th className="border border-slate-300 px-3 py-2 text-left">Детали</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((appointment) => {
              const hasAppointmentComment = Boolean(appointment.appointment_comment?.trim())
              const hasCardComment = Boolean(appointment.card_comment?.trim())
              const fallbackNote = appointment.note?.trim()

              return (
                <tr key={appointment.id} className="align-top odd:bg-white even:bg-slate-50/30">
                  <td className="border border-slate-200 px-3 py-2 text-slate-700">{appointment.date}</td>
                  <td className="border border-slate-200 px-3 py-2 text-slate-700">
                    {appointment.start_time} - {appointment.end_time}
                  </td>
                  <td className="border border-slate-200 px-3 py-2">
                    {appointment.visit_label || appointment.appointment_status_label ? (
                      <span className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${visitBadgeClasses(appointment.visit_kind)}`}>
                        <span className={`h-2.5 w-2.5 rounded-[3px] ${appointmentStatusColor(appointment.appointment_status_code, appointment.appointment_status_label)}`} />
                        {appointment.appointment_status_label ?? appointment.visit_label}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="border border-slate-200 px-3 py-2 font-medium text-slate-900">
                    {appointment.doctor_name?.trim() || 'Не указан'}
                  </td>
                  <td className="border border-slate-200 px-3 py-2 text-slate-900">
                    {canIssueSecretLinks && onIssuePatientSecretLink && appointment.patient_id ? (
                      <button
                        type="button"
                        className="text-left text-sky-700 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-slate-400"
                        onClick={() => void handleIssuePatientLink(appointment.patient_id!, appointment.patient_name)}
                        disabled={issuingPatientId === appointment.patient_id}
                        title="Сформировать secret-ссылку входа для пациента"
                      >
                        {issuingPatientId === appointment.patient_id ? 'Формируем secret...' : appointment.patient_name}
                      </button>
                    ) : (
                      appointment.patient_name
                    )}
                  </td>
                  <td className="border border-slate-200 px-3 py-2 text-slate-700">{appointment.service_name}</td>
                  <td className="border border-slate-200 px-3 py-2 text-slate-600">
                    <div className="leading-5">
                      <div className="text-slate-900">{appointment.branch_name}</div>
                      <div>
                        {appointment.room_name} · {formatStatusLabel(appointment.status)}
                        {appointment.appointment_state ? ` · ${appointment.appointment_state}` : ''}
                        {appointment.appointment_status_code != null ? ` · код ${appointment.appointment_status_code}` : ''}
                      </div>
                      {hasAppointmentComment ? (
                        <div className="mt-1 whitespace-pre-wrap break-words">Комментарий к приему: {appointment.appointment_comment}</div>
                      ) : null}
                      {hasCardComment ? (
                        <div className="mt-1 whitespace-pre-wrap break-words">Комментарий к карте: {appointment.card_comment}</div>
                      ) : null}
                      {!hasAppointmentComment && !hasCardComment && fallbackNote ? (
                        <div className="mt-1 whitespace-pre-wrap break-words">{fallbackNote}</div>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {appointments.length === 0 ? (
        <div className="px-6 py-8 text-sm text-slate-500">На выбранный период записей нет.</div>
      ) : null}
      {issuedLink ? (
        <SecretLoginLinkDialog
          url={issuedLink.url}
          targetName={issuedLink.targetName}
          onClose={() => setIssuedLink(null)}
        />
      ) : null}
    </section>
  )
}
